// icons
import email from '@iconify/icons-carbon/email';
import phone from '@iconify/icons-carbon/phone';
import { Avatar, Button, Container, Divider, Grid, Link, Stack, Typography } from '@mui/material';
// @mui
import { styled } from '@mui/material/styles';
import { Box } from '@mui/system';
import imageUrlFor from '@utils/image';
import { m } from "framer-motion";
import NextLink from 'next/link';
import { ServiceProps } from 'src/@types/service';
import { useResponsive } from 'src/hooks';
import { Breadcrumbs, Iconify, MotionViewport, TextIconLabel, varFade } from '../../components';
import Image from '../../components/NextSanityImage';
// routes
import Routes from '../../routes';

// ----------------------------------------------------------------------

const RootStyle = styled('div')(({ theme }) => ({
  paddingTop: theme.spacing(5),
  paddingBottom: theme.spacing(10),
  backgroundColor: theme.palette.background.neutral,
}));

// ----------------------------------------------------------------------

type Props = {
  service: ServiceProps;
};

export default function ServiceHero({ service }: Props) {
  const { title, header, slug, description, contact } = service;
  const isDesktop = useResponsive('up', 'sm');

  return (
    <RootStyle>
      <Container>
        <Breadcrumbs
          links={[
            { name: 'Hjem', href: '/' },
            { name: 'Tjenester', href: Routes.services },
            { name: service.title || '' },
          ]}
          sx={{ mb: 8 }}
        />

        <Grid
          container
          rowSpacing={{ xs: 6, md: 0 }}
          columnSpacing={{ md: 10 }}
          direction="row-reverse"
        >
          <Grid item xs={12} md={5}>
            <Image alt={header.alt || "header"} src={header} ratio="1/1" sx={{ borderRadius: 2 }} />
          </Grid>

          <Grid item xs={12} md={7}>
            <Stack spacing={3}>
              <Stack spacing={2} alignItems="flex-start" component={MotionViewport}>
                <Typography variant="h3" variants={varFade().inUp} component={m.h1}>
                  {title}
                </Typography>
                <Typography variant="overline" sx={{ color: 'secondary.main' }}>
                  Info
                </Typography>
                <Typography sx={{ color: 'text.secondary' }}>{description}</Typography>
              </Stack>

              <Divider sx={{ borderStyle: 'dashed' }} />

              <Stack
                spacing={{ xs: 2.5, sm: 1.5 }}
                direction={{ xs: 'column', sm: 'row' }}
                alignItems="center"
                divider={
                  isDesktop && <Divider orientation="vertical" sx={{ height: 20, my: 'auto' }} />
                }
              >
                <TextIconLabel
                  icon={
                    <Avatar
                      sx={{ width: 40, height: 40 }}
                      alt={contact.name + " profilbilde"}
                      src={imageUrlFor(contact.profile, 50).url() || ""}
                    />
                  }
                  value={
                    <>
                      <Typography variant="subtitle2" sx={{ ml: 2, mr: 0.5 }}>
                        {contact.name}
                      </Typography>
                    </>
                  }
                />
                <Stack
                  direction="row"
                  spacing={1.5}
                  divider={<Divider orientation="vertical" sx={{ height: 20, my: 'auto' }} />}
                >
                  <Stack spacing={1} direction="row" alignItems="center">
                    <Iconify icon={phone} sx={{ width: 22, height: 22 }} />
                    <Link
                      href={'tel:' + contact.phone}
                      color="inherit"
                      underline="none"
                      variant="subtitle2"
                    >
                      {contact.phone}
                    </Link>
                  </Stack>
                  <Stack spacing={1} direction="row" alignItems="center">
                    <Iconify icon={email} sx={{ width: 22, height: 22 }} />
                    <Link
                      href={'mailto:' + contact.email}
                      color="inherit"
                      underline="none"
                      variant="subtitle2"
                    >
                      {contact.email}
                    </Link>
                  </Stack>
                </Stack>
              </Stack>

              <Box sx={{ width: { xs: '100%' }, display: { xs: 'grid', sm: 'block' } }}>
                <NextLink href={`/form/?service=${slug.current}`} passHref>
                  <Button size="large" variant="contained">
                    Benytt kontaktskjema
                  </Button>
                </NextLink>
              </Box>
            </Stack>
          </Grid>
        </Grid>
      </Container>
    </RootStyle>
  );
}
